import React, { useState } from 'react';
import API from '../services/api';
import { 
  Button, 
  Input, 
  Card, 
  Row, 
  Col, 
  Table, 
  Typography, 
  Space,
  Spin,
  message,
  Modal
} from 'antd';
import { SearchOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { confirm } = Modal;

// 将字节转换为 Gbps
const convertBytesToGbps = (bytes) => {
  const bits = bytes * 8; // 将字节转换为位
  const gbps = bits / 1e9; // 转换为 Gbps
  return gbps.toFixed(2); // 保留两位小数
};

// 将秒转换为更大单位（分钟、小时、天）
const convertSeconds = (seconds) => {
  if (seconds >= 86400) {
    const days = (seconds / 86400).toFixed(2);
    return `${days} 天`;
  } else if (seconds >= 3600) {
    const hours = (seconds / 3600).toFixed(2);
    return `${hours} 小时`;
  } else if (seconds >= 60) {
    const minutes = (seconds / 60).toFixed(2);
    return `${minutes} 分钟`;
  }
  return `${seconds} 秒`;
};

const IpTowLookup = () => {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);


  const handleChange = (e) => {
    setIp(e.target.value);
  };

  const handleSubmit = async () => {
    if (!ip.trim()) {
      message.warning('请输入IP地址');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await API.get(`/api/admin/ip-tow-lookup`, {
        params: { ip },
      });
      setResult(response.data);
      if (Array.isArray(response.data) && response.data.length > 0) {
        message.success('查询成功');
      } else {
        message.info('没有找到相关牵引状态信息');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '查询失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  // 处理解除牵引操作
  const handleDelIpTow = async (targetIp) => {
    confirm({
      title: '确认操作',
      icon: <ExclamationCircleOutlined />,
      content: `您确定要解除 IP ${targetIp} 的牵引吗？此操作请谨慎使用！`,
      okText: '确认解除牵引',
      okType: 'danger',
      cancelText: '取消',
      async onOk() {
        const hide = message.loading('正在解除牵引...', 0);
        try {
          const response = await API.post(`/api/admin/del-ip-tow`, {
            ip: targetIp,
          });
          hide();
          message.success(response.data.msg || '解除牵引成功');
          // 重新查询以更新状态
          if (ip === targetIp) {
            handleSubmit();
          }
        } catch (error) {
          hide();
          message.error(error.response?.data?.message || '解除牵引失败');
        }
      },
    });
  };

  const columns = [
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      align: 'center',
      width: 120,
    },
    {
      title: '牵引次数',
      dataIndex: 'count',
      key: 'count',
      align: 'center',
      width: 80,
    },
    {
      title: '访问流量(Gbps)',
      dataIndex: 'visitFlow',
      key: 'visitFlow',
      align: 'center',
      width: 120,
      render: (value) => convertBytesToGbps(value),
    },
    {
      title: '访问包数',
      dataIndex: 'visitPack',
      key: 'visitPack',
      align: 'center',
      width: 100,
    },
    {
      title: '持续时间',
      dataIndex: 'duration_time',
      key: 'duration_time',
      align: 'center',
      width: 100,
      render: (value) => convertSeconds(value),
    },
    {
      title: '牵引命令',
      dataIndex: 'towCmdName',
      key: 'towCmdName',
      align: 'center',
      width: 100,
    },
    {
      title: '开始时间',
      dataIndex: 'towTime',
      key: 'towTime',
      align: 'center',
      width: 130,
    },
    {
      title: '结束时间',
      dataIndex: 'unTowTime',
      key: 'unTowTime',
      align: 'center',
      width: 130,
    },
    {
      title: '下次持续时间',
      dataIndex: 'next_time',
      key: 'next_time',
      align: 'center',
      width: 120,
      render: (value) => convertSeconds(value),
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDelIpTow(record.ip)}
        >
          解除牵引
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="center">
        <Col xs={24} md={16} lg={12}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
            查询 IP 牵引状态
          </Title>
          
          <Card style={{ marginBottom: 32 }}>
            <Space orientation="vertical" style={{ width: '100%' }} size="large">
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  输入 IP 地址
                </label>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={ip}
                    onChange={handleChange}
                    placeholder="请输入 IP 地址"
                    onPressEnter={handleSubmit}
                    prefix={<SearchOutlined />}
                  />
                  <Button 
                    type="primary" 
                    onClick={handleSubmit} 
                    loading={loading}
                    icon={!loading && <SearchOutlined />}
                  >
                    查询
                  </Button>
                </Space.Compact>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {result && (
        <Row justify="center">
          <Col xs={24} sm={22} md={20} lg={18} xl={16}>
            <Card 
              title={
                <span style={{ color: '#1890ff' }}>
                  <SearchOutlined style={{ marginRight: 8 }} />
                  查询结果
                </span>
              }
            >
              <Spin spinning={loading}>
                <Table
                  columns={columns}
                  dataSource={Array.isArray(result) ? result : []}
                  rowKey={(record, index) => index}
                  pagination={false}
                  bordered
                  size="small"
                  scroll={{ x: 1200 }}
                  locale={{
                    emptyText: '没有找到相关牵引状态信息'
                  }}
                />
              </Spin>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default IpTowLookup;
